/* eslint-disable fp/no-loops */
import { Fragment, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'next-i18next'
import { v4 as uuid } from 'uuid'

import Input from '~/bootcamp/Activities/components/Input'
import Times from '~/bootcamp/Activities/components/Times'
import { ActivityProps } from '~/bootcamp/Activities/types/activities.types'
import { TimesTableCell, TimesTableCellStatus } from '~/bootcamp/types/bootcamp.types'
import { randomIntFromInterval, hocOnEnterPress } from '~/bootcamp/Activities/utils/activities.utils'
import useFocus from '~/bootcamp/Activities/hooks/useFocus'
import { useEmitInstance } from '~/bootcamp/Activities/utils/instance.utils'

import TableCell from './TableCell'
import CellHeading from './CellHeading'

import styles from './TimesTable4x4.module.css'

const TimesTable4x4 = ({
  onNextQuestionClick,
  answerState,
  onAnswerStateChange,
  onInstanceChange,
  actionButtonsFC,
  autofocus = true,
}: ActivityProps): React.ReactElement => {
  const [cells, addCells] = useState<TimesTableCell[]>([]) // Cells array e.g. [{x: 3, y:1}]
  const [completedCells, setCompletedCells] = useState<number[]>([]) // Completed cells index e.g. [4,5,0]
  const [activeCell, setActiveCell] = useState<number>(0) // Selected/Active cell index
  const [xInput, setXInput] = useState<string>() // X field value
  const [yInput, setYInput] = useState<string>() // Y field value
  const [inputKey, setInputKey] = useState<string>(uuid())
  const inputRef = useRef<HTMLInputElement>(null)
  const { t } = useTranslation('generics')
  useFocus(autofocus, answerState, inputRef)

  useEffect(() => {
    // Generate cells
    // Number of cells on every row/column = 4
    const generatedCells = []

    for (let x = 1; x <= 4; x++) {
      for (let y = 1; y <= 4; y++) {
        // eslint-disable-next-line fp/no-mutating-methods
        generatedCells.push({
          x,
          y,
        })
      }
    }

    addCells(generatedCells)
    setActiveCell(randomIntFromInterval(0, 15, []))
  }, [])

  // The active cell's row and column are the answer and ride only the guarded channel.
  const activeCellData = cells[activeCell]
  useEmitInstance(
    onInstanceChange,
    activeCellData ? 'a 4 by 4 times table with one empty cell selected' : null,
    activeCellData ? `${activeCellData.x} \\cdot ${activeCellData.y}` : null,
  )

  // Select a new cell
  const selectNewCell = (): void => {
    /* Flag for checking all squares/boxes are completed. */
    const isAllBoxCompleted = completedCells.length === 16

    let randomSelection

    /* Reset completed cells when all squares/boxes are completed. */
    if (isAllBoxCompleted) {
      setCompletedCells([])
      randomSelection = randomIntFromInterval(0, 15, [])
    } else {
      randomSelection = randomIntFromInterval(0, 15, completedCells)
    }

    resetUserInput()
    setActiveCell(randomSelection)

    onAnswerStateChange(null)
    onNextQuestionClick()
    setInputKey(uuid())
  }

  const markCellComplete = (): void => {
    if (completedCells.length < 16) {
      setCompletedCells((c) => [...c, activeCell])
    }
  }

  const resetUserInput = (): void => {
    setXInput(undefined) // Reset x input
    setYInput(undefined) // Reset y input

    onAnswerStateChange(null)
  }

  const handleOnCheckAnswerClick = (): void => {
    // Return if any input field is blank
    if (!(xInput && yInput)) return

    const { x, y } = cells[activeCell]

    const userResult = parseInt(xInput, 10) * parseInt(yInput, 10)
    const actualResult = x * y

    if (userResult === actualResult) {
      markCellComplete() // Complete cell
    }

    onAnswerStateChange(userResult === actualResult)
  }

  // Change handler of X input field
  const handleXinputChange = (val: string): void => {
    setXInput(val)
  }

  // Change handler of Y input field
  const handleYinputChange = (val: string): void => {
    setYInput(val)
  }

  const disabledInput = answerState === false

  const onEnterPress = (): void => {
    if (!!xInput && !!yInput) {
      hocOnEnterPress(answerState, handleOnCheckAnswerClick, selectNewCell)
    }
  }

  return (
    <Fragment>
      <div className={`mx-auto ${styles['times-table']}`}>
        <div className="grid gap-1 grid-cols-4 pl-6">
          <CellHeading value="1" />
          <CellHeading value="2" />
          <CellHeading value="3" />
          <CellHeading value="4" />
        </div>
        <div className="flex">
          <div className="grid gap-1">
            <CellHeading alignment="vertical" value="1" />
            <CellHeading alignment="vertical" value="2" />
            <CellHeading alignment="vertical" value="3" />
            <CellHeading alignment="vertical" value="4" />
          </div>
          <div className="grid grid-cols-4 gap-1 flex-1">
            {cells.map((cell, i) => (
              <TableCell
                cell={cell}
                key={`${cell.x}-${cell.y}`}
                onEnterPress={onEnterPress}
                onXchange={handleXinputChange}
                onYchange={handleYinputChange}
                status={generateCellStatus(activeCell, completedCells, answerState === false, i)}
                xInput={xInput}
                yInput={yInput}
              />
            ))}
          </div>
        </div>

        <div className="flex my-6 items-center justify-center">
          <Input
            aria-label={t('ariaLabelButtons.insertFactor')}
            autoFocus={autofocus}
            disabled={disabledInput}
            key={`${inputKey}-first`}
            maxDigits={1}
            onChange={handleXinputChange}
            onEnter={onEnterPress}
            ref={inputRef}
            type="number"
            value={xInput ? xInput.toString() : ''}
          />
          <div className="font-semibold text-2xl text-purple">
            <Times />
          </div>
          <Input
            aria-label={t('ariaLabelButtons.insertFactor')}
            disabled={disabledInput}
            key={`${inputKey}-second`}
            maxDigits={1}
            onChange={handleYinputChange}
            onEnter={onEnterPress}
            type="number"
            value={yInput ? yInput.toString() : ''}
          />
        </div>
        {actionButtonsFC &&
          actionButtonsFC({
            formValid: !!xInput && !!yInput,
            correctAnswer: answerState ?? false,
            submitAnswerAction: handleOnCheckAnswerClick,
            clearFormAction: resetUserInput,
            goToNextQuestionAction: selectNewCell,
          })}
      </div>
    </Fragment>
  )
}

function generateCellStatus(
  activeCell: number,
  completedCells: number[],
  hasWrongAnswer: boolean,
  cellIndex: number,
): TimesTableCellStatus {
  if (hasWrongAnswer && activeCell === cellIndex) return TimesTableCellStatus.Wrong
  if (completedCells.includes(cellIndex)) return TimesTableCellStatus.Completed
  if (activeCell === cellIndex) return TimesTableCellStatus.Selected
  return TimesTableCellStatus.Default
}

export default TimesTable4x4
